import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Lock, File, Folder, Loader2, Eye, KeyRound } from 'lucide-react';
import { api } from '../lib/api';
import { formatBytes } from '../lib/utils';
import toast from 'react-hot-toast';
import { Spotlight } from '../components/aceternity/Spotlight';
import { Meteors } from '../components/aceternity/Meteors';
import { Sparkles } from '../components/aceternity/Sparkles';

export function ShareView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [passwordInput, setPasswordInput] = useState('');
  // triedPassword: 已提交给接口的密码；null = 首次以无密码尝试
  const [triedPassword, setTriedPassword] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['public-share', code, triedPassword],
    queryFn: () => api.publicShare.get(code!, triedPassword || undefined),
    enabled: !!code,
    retry: false,
  });

  const data = query.data;
  const isError403 = (query.error as any)?.status === 403;
  // 是否需要输入密码：仅当"无密码尝试失败(403)"或"密码提交后仍 403"
  const showPasswordPrompt = isError403 && !query.isLoading;

  const confirmPassword = () => {
    const pw = passwordInput.trim();
    if (!pw) { toast.error('请输入访问密码'); return; }
    setTriedPassword(pw);
  };

  const download = (inline = false) => {
    if (!code || !data) return;
    const url = api.publicShare.downloadUrl(code, triedPassword || undefined, inline);
    if (inline) { window.open(url, '_blank'); return; }
    // 使用浏览器原生下载：交给服务端 Content-Disposition，流式保存，避免大文件占内存
    const a = document.createElement('a');
    a.href = url;
    a.download = data.name;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { a.remove(); }, 1000);
  };

  const loading = query.isLoading;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Aceternity 背景效果 */}
      <Spotlight />
      <Meteors number={14} />
      <Sparkles count={26} className="z-0" />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-bg/20 via-transparent to-bg/60" />
      <div className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-primary/30 blur-[120px] animate-float" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-accent-cyan/25 blur-[120px] animate-float" style={{ animationDelay: '1.2s' }} />
      <div className="pointer-events-none absolute top-1/3 left-1/3 w-72 h-72 rounded-full bg-accent-violet/20 blur-[110px] animate-float" style={{ animationDelay: '2s' }} />

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8 animate-fadeInUp">
          <span className="relative inline-flex items-center justify-center w-18 h-18 p-4 rounded-3xl brand-gradient glow-primary mb-4 animate-float">
            <Folder className="w-9 h-9 text-white" />
          </span>
          <h1 className="text-3xl font-black neon-text tracking-tight">红卫兵网盘</h1>
          <p className="text-[10px] font-mono tracking-[0.35em] text-text-muted mt-2">FILE SHARE</p>
        </div>

        <div className="neon-border soft-shadow animate-fadeInUp">
          <div className="p-6 dot-grid">
          {/* 加载中 */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-10 text-text-muted gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <p>正在加载分享...</p>
            </div>
          )}

          {/* 验证成功 -> 显示文件与下载 */}
          {!loading && data && (
            <>
              <div className="text-center pb-5">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-primary/10 mb-4 animate-scaleIn">
                  {data.mimeType?.startsWith('image/') ? <Eye className="w-9 h-9 text-primary" /> : <File className="w-9 h-9 text-primary" />}
                </div>
                <p className="text-text font-medium break-all">{data.name}</p>
                <p className="text-text-muted text-sm mt-0.5">{formatBytes(data.size)}</p>
                {triedPassword && (
                  <p className="inline-flex items-center justify-center gap-1 text-xs text-success mt-3 px-3 py-1 rounded-full bg-success/10 animate-fadeIn">
                    <KeyRound className="w-3 h-3" /> 密码验证通过
                  </p>
                )}
              </div>
              <div className="border-t border-border-soft pt-4 space-y-2">
                <button onClick={() => download(false)} className="w-full flex items-center justify-center gap-2 py-3 brand-gradient hover:brightness-110 text-white rounded-xl font-medium soft-shadow btn-press transition-all">
                  <Download className="w-4 h-4" /> 下载文件
                </button>
                {data.mimeType?.startsWith('image/') && (
                  <button onClick={() => download(true)} className="w-full flex items-center justify-center gap-2 py-3 bg-card/70 hover:bg-card text-text rounded-xl transition-colors btn-press">在线预览</button>
                )}
              </div>
            </>
          )}

          {/* 需要密码 */}
          {!loading && !data && showPasswordPrompt && (
            <div className="text-center py-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-2xl bg-warning/10 border border-warning/20 mb-4 animate-scaleIn">
                <Lock className="w-7 h-7 text-warning" />
              </div>
              <p className="text-text font-medium mb-1">此分享需要访问密码</p>
              {triedPassword !== null && (
                <p className="text-xs text-danger animate-fadeIn mt-1">访问密码错误，请重新输入</p>
              )}
              <div className="space-y-3 max-w-xs mx-auto mt-4">
                <input
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && confirmPassword()}
                  type="password"
                  placeholder="输入访问密码"
                  autoFocus
                  className="w-full px-4 py-2.5 bg-bg/60 border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                />
                <button onClick={confirmPassword} className="w-full py-2.5 brand-gradient hover:brightness-110 text-white rounded-xl font-medium btn-press soft-shadow transition-all">
                  确认
                </button>
              </div>
            </div>
          )}

          {/* 其他错误：不存在/过期/已删除 */}
          {!loading && !data && !showPasswordPrompt && (
            <div className="text-center py-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-2xl bg-card border border-border-soft mb-4">
                <File className="w-7 h-7 text-text-muted" />
              </div>
              <p className="text-text font-medium mb-1">无法访问此分享</p>
              <p className="text-text-muted text-sm mb-4">链接可能已失效、过期或文件已被删除</p>
              <button onClick={() => navigate('/files')} className="px-4 py-2 brand-gradient text-white rounded-xl btn-press soft-shadow transition-all hover:brightness-110">去登录</button>
            </div>
          )}
          </div>
        </div>

        <p className="text-center text-xs text-text-muted mt-6">由红卫兵网盘提供安全的文件分享</p>
      </div>
    </div>
  );
}
